import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { 
  RefreshCw, Download, Upload, ShoppingCart, Package, Truck, 
  CheckCircle2, XCircle, ArrowRightLeft, Search, Filter,
  MoreHorizontal, Eye, Store, Clock, Settings, Play, Pause, Trash2,
  Zap, CheckCircle, AlertCircle, X
} from "lucide-react";

const API = "/api";

const MARKETPLACE_NAMES = {
  trendyol: "Trendyol",
  hepsiburada: "HepsiBurada",
  n11: "N11",
  ciceksepeti: "Çiçeksepeti",
  noon: "NOON",
  namshi: "NAMSHI",
  amazon: "Amazon"
};

const MARKETPLACE_COLORS = {
  trendyol: "bg-orange-500",
  hepsiburada: "bg-orange-600",
  n11: "bg-green-500",
  ciceksepeti: "bg-pink-500",
  noon: "bg-yellow-500",
  namshi: "bg-purple-500",
  amazon: "bg-amber-500"
};

const STATUS_CONFIG = {
  pending: { label: "Beklemede", color: "bg-yellow-100 text-yellow-700", icon: "⏳" },
  processing: { label: "Hazırlanıyor", color: "bg-blue-100 text-blue-700", icon: "📦" },
  shipped: { label: "Kargoda", color: "bg-purple-100 text-purple-700", icon: "🚚" },
  delivered: { label: "Teslim Edildi", color: "bg-green-100 text-green-700", icon: "✅" },
  cancelled: { label: "İptal", color: "bg-red-100 text-red-700", icon: "❌" },
  returned: { label: "İade", color: "bg-gray-100 text-gray-700", icon: "↩️" }
};

export default function MarketplaceOrdersPage() {
  const { token } = useAuth();
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [pulling, setPulling] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  // Filtreler
  const [filterMarketplace, setFilterMarketplace] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterSynced, setFilterSynced] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  
  // Seçim
  const [selectedOrders, setSelectedOrders] = useState([]);
  
  // Modal
  const [statusModal, setStatusModal] = useState({ open: false, order: null });
  const [statusUpdate, setStatusUpdate] = useState({
    status: "",
    tracking_number: "",
    tracking_company: "",
    note: ""
  });
  const [updatingStatus, setUpdatingStatus] = useState(false);

  // Scheduler
  const [schedulerSettings, setSchedulerSettings] = useState({
    enabled: false,
    pull_interval_minutes: 15,
    auto_sync_to_shopify: false,
    marketplaces: [],
    pull_order_statuses: ["pending", "processing"]
  });
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [savingScheduler, setSavingScheduler] = useState(false);
  const [showSchedulerSettings, setShowSchedulerSettings] = useState(false);
  
  // Auto Sync
  const [autoSyncing, setAutoSyncing] = useState(false);
  const [autoSyncResult, setAutoSyncResult] = useState(null);
  const [selectedMarketplace, setSelectedMarketplace] = useState("all");
  const [daysBack, setDaysBack] = useState(1);

  useEffect(() => {
    fetchData();
    fetchSchedulerSettings();
  }, [token, filterMarketplace, filterStatus, filterSynced]);

  const fetchSchedulerSettings = async () => {
    try {
      const [settingsRes, statusRes] = await Promise.all([
        axios.get(`${API}/marketplace-orders/scheduler/settings?token=${token}`),
        axios.get(`${API}/marketplace-orders/scheduler/status?token=${token}`)
      ]);
      setSchedulerSettings(settingsRes.data);
      setSchedulerStatus(statusRes.data);
    } catch (error) {
      console.error("Scheduler settings error:", error);
    }
  };

  const saveSchedulerSettings = async () => {
    setSavingScheduler(true);
    try {
      await axios.put(`${API}/marketplace-orders/scheduler/settings?token=${token}`, schedulerSettings);
      toast.success(schedulerSettings.enabled ? "Otomatik sipariş çekme aktif" : "Otomatik sipariş çekme devre dışı");
      fetchSchedulerSettings();
    } catch (error) {
      toast.error("Ayarlar kaydedilemedi");
    } finally {
      setSavingScheduler(false);
    }
  };

  const runSchedulerNow = async () => {
    setPulling(true);
    try {
      await axios.post(`${API}/marketplace-orders/scheduler/run-now?token=${token}`);
      toast.success("Siparişler çekildi");
      fetchData();
      fetchSchedulerSettings();
    } catch (error) {
      toast.error("Hata oluştu");
    } finally {
      setPulling(false);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ token });
      if (filterMarketplace !== "all") params.append("marketplace", filterMarketplace);
      if (filterStatus !== "all") params.append("status", filterStatus);
      if (filterSynced !== "all") params.append("synced", filterSynced === "synced");
      
      const [ordersRes, statsRes] = await Promise.all([
        axios.get(`${API}/marketplace-orders?${params.toString()}`),
        axios.get(`${API}/marketplace-orders/stats?token=${token}`)
      ]);
      
      setOrders(ordersRes.data.orders);
      setStats(statsRes.data);
    } catch (error) {
      toast.error("Veriler yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const pullOrders = async () => {
    setPulling(true);
    try {
      // Trendyol'dan siparişleri çek (son 30 gün)
      const trendyolRes = await axios.post(`${API}/trendyol/pull-orders?token=${token}`);
      const trendyolPulled = trendyolRes.data.pulled || 0;
      
      if (trendyolPulled > 0) {
        toast.success(`${trendyolPulled} yeni sipariş çekildi`);
      } else {
        toast.info("Yeni sipariş bulunamadı");
      }
      
      fetchData();
    } catch (error) {
      console.error("Pull orders error:", error);
      toast.error("Siparişler çekilemedi");
    } finally {
      setPulling(false);
    }
  };

  const deleteAllOrders = async () => {
    if (!window.confirm("Tüm siparişleri silmek istediğinizden emin misiniz? Bu işlem geri alınamaz!")) {
      return;
    }

    setDeleting(true);
    try {
      const response = await axios.delete(`${API}/marketplace-orders/delete-all?token=${token}`);
      toast.success(`${response.data.deleted} sipariş silindi`);
      setSelectedOrders([]);
      fetchData();
    } catch (error) {
      toast.error("Silme işlemi başarısız");
    } finally {
      setDeleting(false);
    }
  };

  const syncToShopify = async () => {
    if (selectedOrders.length === 0) {
      toast.error("Lütfen en az bir sipariş seçin");
      return;
    }

    setSyncing(true);
    try {
      // Backend {"order_ids": [...]} formatı bekliyor
      const response = await axios.post(`${API}/marketplace-orders/sync-to-shopify?token=${token}`, {
        order_ids: selectedOrders
      });
      const { synced, skipped, errors, store, total_selected } = response.data;
      
      if (synced > 0) {
        toast.success(`${synced}/${total_selected || selectedOrders.length} sipariş Shopify'a aktarıldı (${store || ""})`);
      }
      
      if (skipped > 0) {
        toast.warning(`${skipped} sipariş atlandı (zaten aktarılmış)`);
      }
      
      if (errors && errors.length > 0) {
        const errorDetails = errors.map(e => typeof e === 'object' ? `${e.order_number || e.id}: ${e.error}` : e).join('\n');
        toast.error(`${errors.length} hata:\n${errorDetails.slice(0, 200)}`);
      }
      
      if (synced === 0 && skipped === 0 && (!errors || errors.length === 0)) {
        toast.warning("Aktarılacak sipariş bulunamadı");
      }
      
      setSelectedOrders([]);
      fetchData();
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      // Handle Pydantic validation errors
      if (Array.isArray(errorDetail)) {
        toast.error(`Hata: ${errorDetail[0]?.msg || "Validasyon hatası"}`);
      } else {
        toast.error(errorDetail || "Aktarım başarısız");
      }
    } finally {
      setSyncing(false);
    }
  };

  const openStatusModal = (order) => {
    setStatusModal({ open: true, order });
    setStatusUpdate({
      status: order.status,
      tracking_number: order.tracking_number || "",
      tracking_company: order.tracking_company || "",
      note: ""
    });
  };

  const updateOrderStatus = async () => {
    if (!statusModal.order) return;
    
    setUpdatingStatus(true);
    try {
      await axios.put(
        `${API}/marketplace-orders/${statusModal.order.id}/status?token=${token}`,
        statusUpdate
      );
      toast.success("Sipariş durumu güncellendi");
      setStatusModal({ open: false, order: null });
      fetchData();
    } catch (error) {
      toast.error("Durum güncellenemedi");
    } finally {
      setUpdatingStatus(false);
    }
  };

  const toggleSelectOrder = (orderId) => {
    setSelectedOrders(prev => 
      prev.includes(orderId)
        ? prev.filter(id => id !== orderId)
        : [...prev, orderId]
    );
  };

  // Otomatik Senkronizasyon (Çek ve Aktar)
  const runAutoSync = async (pullFirst = true) => {
    setAutoSyncing(true);
    setAutoSyncResult(null);
    
    try {
      const response = await axios.post(
        `${API}/marketplace-orders/auto-sync?token=${token}&marketplace=${selectedMarketplace}&limit=50&pull_first=${pullFirst}&days_back=${daysBack}`,
        {},
        { timeout: 600000 }  // 10 dakika timeout
      );
      
      setAutoSyncResult(response.data);
      
      if (response.data.synced > 0) {
        toast.success(`✅ ${response.data.synced} sipariş Shopify'a aktarıldı`);
      }
      if (response.data.pulled > 0) {
        toast.info(`📦 ${response.data.pulled} yeni sipariş çekildi`);
      }
      if (response.data.errors?.length > 0) {
        toast.warning(`⚠️ ${response.data.errors.length} sipariş aktarılamadı`);
      }
      if (response.data.synced === 0 && response.data.pulled === 0 && !response.data.errors?.length) {
        toast.info("ℹ️ Aktarılacak yeni sipariş bulunamadı");
      }
      
      // Verileri yenile
      fetchData();
    } catch (error) {
      console.error("Auto sync error:", error);
      let errorMsg = "Otomatik senkronizasyon başarısız";
      
      if (error.code === 'ECONNABORTED') {
        errorMsg = "İşlem zaman aşımına uğradı. Arka planda devam ediyor olabilir.";
        toast.warning(errorMsg);
      } else if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        errorMsg = Array.isArray(detail) ? detail[0]?.msg : detail;
        toast.error(errorMsg);
      } else {
        toast.error(errorMsg);
      }
      
      setAutoSyncResult({ success: false, error: errorMsg });
    } finally {
      setAutoSyncing(false);
    }
  };

  const selectAllOrders = () => {
    if (selectedOrders.length === filteredOrders.length) {
      setSelectedOrders([]);
    } else {
      setSelectedOrders(filteredOrders.map(o => o.id));
    }
  };

  const filteredOrders = orders.filter(order => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        order.order_number?.toLowerCase().includes(query) ||
        order.customer_name?.toLowerCase().includes(query) ||
        order.marketplace_order_id?.toLowerCase().includes(query)
      );
    }
    return true;
  });

  const unsyncedOrders = filteredOrders.filter(o => !o.synced_to_shopify);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in" data-testid="marketplace-orders-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Pazaryeri Siparişleri</h1>
          <p className="text-slate-500 mt-1">Pazaryerlerinden gelen siparişleri Shopify'a aktarın</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setShowSchedulerSettings(!showSchedulerSettings)} variant="outline" data-tour="scheduler-btn">
            <Settings className="w-4 h-4 mr-2" />
            Zamanlayıcı
          </Button>
          <Button 
            onClick={deleteAllOrders} 
            variant="outline"
            disabled={deleting || orders.length === 0}
            className="text-red-600 border-red-200 hover:bg-red-50"
            data-testid="delete-all-orders-btn"
          >
            {deleting ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
            Tümünü Sil
          </Button>
          <Button 
            onClick={syncToShopify} 
            disabled={syncing || selectedOrders.length === 0}
            className="bg-green-600 hover:bg-green-700"
            data-tour="sync-shopify-btn"
          >
            {syncing ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
            Shopify'a Aktar ({selectedOrders.length})
          </Button>
        </div>
      </div>

      {/* Çek ve Aktar Paneli */}
      <Card className="border-purple-200 bg-purple-50/30">
        <CardContent className="py-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Pazaryeri</Label>
              <Select value={selectedMarketplace} onValueChange={setSelectedMarketplace}>
                <SelectTrigger className="w-[150px] h-9 bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tümü</SelectItem>
                  <SelectItem value="trendyol">Trendyol</SelectItem>
                  <SelectItem value="n11">N11</SelectItem>
                  <SelectItem value="ciceksepeti">Çiçeksepeti</SelectItem>
                  <SelectItem value="hepsiburada">HepsiBurada</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Kaç Gün Geriye</Label>
              <Select value={String(daysBack)} onValueChange={(v) => setDaysBack(Number(v))}>
                <SelectTrigger className="w-[130px] h-9 bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Son 1 gün</SelectItem>
                  <SelectItem value="3">Son 3 gün</SelectItem>
                  <SelectItem value="7">Son 7 gün</SelectItem>
                  <SelectItem value="14">Son 14 gün</SelectItem>
                  <SelectItem value="30">Son 30 gün</SelectItem>
                  <SelectItem value="60">Son 2 ay</SelectItem>
                  <SelectItem value="90">Son 3 ay</SelectItem>
                  <SelectItem value="180">Son 6 ay</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button 
              onClick={() => runAutoSync(true)} 
              disabled={autoSyncing}
              className="bg-purple-600 hover:bg-purple-700 h-9"
            >
              {autoSyncing ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
              Çek ve Aktar
            </Button>
            <Button 
              onClick={() => runAutoSync(false)} 
              variant="outline"
              disabled={autoSyncing}
              className="border-purple-300 text-purple-700 hover:bg-purple-100 h-9"
              data-testid="auto-sync-btn"
            >
              {autoSyncing ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Zap className="w-4 h-4 mr-2" />}
              Sadece Aktar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Auto Sync Sonuç Gösterimi */}
      {autoSyncResult && (
        <Card className={`border-2 ${autoSyncResult.success ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}>
          <CardContent className="py-4">
            <div className="flex items-start justify-between">
              <div>
                <h4 className="font-semibold flex items-center gap-2">
                  {autoSyncResult.success ? (
                    <><CheckCircle className="w-5 h-5 text-green-600" /> Otomatik Senkronizasyon Tamamlandı</>
                  ) : (
                    <><AlertCircle className="w-5 h-5 text-red-600" /> Senkronizasyon Hatası</>
                  )}
                </h4>
                {autoSyncResult.steps && (
                  <ul className="mt-2 space-y-1 text-sm">
                    {autoSyncResult.steps.map((step, i) => (
                      <li key={i} className="text-slate-600">{step}</li>
                    ))}
                  </ul>
                )}
                {autoSyncResult.error && (
                  <p className="mt-2 text-sm text-red-600">{autoSyncResult.error}</p>
                )}
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setAutoSyncResult(null)}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Scheduler Ayarları */}
      {showSchedulerSettings && (
        <Card className="border-blue-200 bg-blue-50/50">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-blue-600" />
                <CardTitle className="text-lg">Otomatik Sipariş Çekme Ayarları</CardTitle>
              </div>
              <div className="flex items-center gap-2">
                <Switch 
                  checked={schedulerSettings.enabled}
                  onCheckedChange={(checked) => setSchedulerSettings(prev => ({...prev, enabled: checked}))}
                />
                <span className="text-sm font-medium">
                  {schedulerSettings.enabled ? "Aktif" : "Devre Dışı"}
                </span>
              </div>
            </div>
            <CardDescription>
              Pazaryerlerinden siparişleri belirli aralıklarla otomatik çek
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Çekme Aralığı</Label>
                <Select 
                  value={String(schedulerSettings.pull_interval_minutes)} 
                  onValueChange={(v) => setSchedulerSettings(prev => ({...prev, pull_interval_minutes: parseInt(v)}))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5">Her 5 dakika</SelectItem>
                    <SelectItem value="10">Her 10 dakika</SelectItem>
                    <SelectItem value="15">Her 15 dakika</SelectItem>
                    <SelectItem value="30">Her 30 dakika</SelectItem>
                    <SelectItem value="60">Her saat</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Otomatik Shopify Aktarımı</Label>
                <div className="flex items-center gap-2 pt-2">
                  <Switch 
                    checked={schedulerSettings.auto_sync_to_shopify}
                    onCheckedChange={(checked) => setSchedulerSettings(prev => ({...prev, auto_sync_to_shopify: checked}))}
                  />
                  <span className="text-sm">
                    {schedulerSettings.auto_sync_to_shopify ? "Aktif" : "Manuel"}
                  </span>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Durum</Label>
                <div className="flex items-center gap-2 pt-2">
                  {schedulerStatus?.job_exists ? (
                    <Badge className="bg-green-100 text-green-700">
                      <CheckCircle2 className="w-3 h-3 mr-1" />
                      Çalışıyor
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-slate-500">
                      Devre Dışı
                    </Badge>
                  )}
                  {schedulerStatus?.last_pull_time && (
                    <span className="text-xs text-slate-500">
                      Son: {new Date(schedulerStatus.last_pull_time).toLocaleString("tr-TR")}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button variant="outline" onClick={runSchedulerNow} disabled={pulling}>
                {pulling ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Play className="w-4 h-4 mr-2" />}
                Şimdi Çalıştır
              </Button>
              <Button onClick={saveSchedulerSettings} disabled={savingScheduler}>
                {savingScheduler && <RefreshCw className="w-4 h-4 animate-spin mr-2" />}
                Kaydet
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* İstatistik Kartları */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <ShoppingCart className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.total || 0}</p>
                <p className="text-sm text-slate-500">Toplam Sipariş</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-yellow-100 rounded-lg">
                <Package className="w-6 h-6 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.pending || 0}</p>
                <p className="text-sm text-slate-500">Bekleyen</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 rounded-lg">
                <CheckCircle2 className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.synced || 0}</p>
                <p className="text-sm text-slate-500">Shopify'a Aktarıldı</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-orange-100 rounded-lg">
                <ArrowRightLeft className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.not_synced || 0}</p>
                <p className="text-sm text-slate-500">Aktarılmamış</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-100 rounded-lg">
                <Store className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{Object.keys(stats.by_marketplace || {}).length}</p>
                <p className="text-sm text-slate-500">Pazaryeri</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filtreler */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-slate-400" />
              <span className="text-sm font-medium text-slate-600">Filtrele:</span>
            </div>
            
            <Select value={filterMarketplace} onValueChange={setFilterMarketplace}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Pazaryeri" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Pazaryerleri</SelectItem>
                {Object.entries(MARKETPLACE_NAMES).map(([key, name]) => (
                  <SelectItem key={key} value={key}>{name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Durum" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Durumlar</SelectItem>
                {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                  <SelectItem key={key} value={key}>{config.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filterSynced} onValueChange={setFilterSynced}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Aktarım" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tümü</SelectItem>
                <SelectItem value="synced">Aktarılmış</SelectItem>
                <SelectItem value="not_synced">Aktarılmamış</SelectItem>
              </SelectContent>
            </Select>

            <div className="flex-1 relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Sipariş no veya müşteri ara..."
                className="pl-10"
              />
            </div>

            <Button variant="outline" size="sm" onClick={fetchData}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Yenile
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Sipariş Tablosu */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle>Siparişler ({filteredOrders.length})</CardTitle>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={selectAllOrders}>
                {selectedOrders.length === filteredOrders.length ? "Seçimi Kaldır" : "Tümünü Seç"}
              </Button>
              {unsyncedOrders.length > 0 && (
                <Badge variant="outline" className="bg-orange-50 text-orange-700">
                  {unsyncedOrders.length} sipariş aktarım bekliyor
                </Badge>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12"></TableHead>
                  <TableHead>Pazaryeri</TableHead>
                  <TableHead>Sipariş No</TableHead>
                  <TableHead>Müşteri</TableHead>
                  <TableHead>Ürünler</TableHead>
                  <TableHead>Toplam</TableHead>
                  <TableHead>Durum</TableHead>
                  <TableHead>Shopify</TableHead>
                  <TableHead>Tarih</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredOrders.map((order) => {
                  const statusConfig = STATUS_CONFIG[order.status] || STATUS_CONFIG.pending;
                  const mpColor = MARKETPLACE_COLORS[order.marketplace] || "bg-gray-500";
                  
                  return (
                    <TableRow key={order.id} className="hover:bg-slate-50">
                      <TableCell>
                        <Checkbox 
                          checked={selectedOrders.includes(order.id)}
                          onCheckedChange={() => toggleSelectOrder(order.id)}
                          disabled={order.synced_to_shopify}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className={`w-8 h-8 ${mpColor} rounded-lg flex items-center justify-center text-white font-bold text-xs`}>
                            {order.marketplace?.substring(0, 2).toUpperCase()}
                          </div>
                          <span className="text-sm font-medium">
                            {MARKETPLACE_NAMES[order.marketplace] || order.marketplace}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="font-mono text-sm">{order.order_number}</TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{order.customer_name}</p>
                          <p className="text-xs text-slate-500">{order.customer_email}</p>
                        </div>
                      </TableCell>
                      <TableCell>{order.items?.length || 0} ürün</TableCell>
                      <TableCell className="font-medium">
                        {order.total?.toFixed(2)} {order.currency}
                      </TableCell>
                      <TableCell>
                        <Badge className={statusConfig.color}>
                          {statusConfig.icon} {statusConfig.label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {order.synced_to_shopify ? (
                          <Badge className="bg-green-100 text-green-700">
                            <CheckCircle2 className="w-3 h-3 mr-1" />
                            {order.shopify_order_number}
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-orange-600">
                            Aktarılmadı
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-slate-500">
                        {new Date(order.created_at).toLocaleDateString("tr-TR")}
                      </TableCell>
                      <TableCell>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => openStatusModal(order)}
                        >
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {filteredOrders.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center py-8 text-slate-500">
                      Sipariş bulunamadı
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Durum Güncelleme Modal */}
      <Dialog open={statusModal.open} onOpenChange={(open) => setStatusModal({ open, order: statusModal.order })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Sipariş Durumu Güncelle</DialogTitle>
            <DialogDescription>
              {statusModal.order?.order_number} numaralı sipariş
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Yeni Durum</Label>
              <Select 
                value={statusUpdate.status} 
                onValueChange={(v) => setStatusUpdate(prev => ({...prev, status: v}))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                    <SelectItem key={key} value={key}>
                      {config.icon} {config.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {(statusUpdate.status === "shipped" || statusUpdate.status === "delivered") && (
              <>
                <div className="space-y-2">
                  <Label>Kargo Firması</Label>
                  <Select 
                    value={statusUpdate.tracking_company} 
                    onValueChange={(v) => setStatusUpdate(prev => ({...prev, tracking_company: v}))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Kargo firması seçin" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="aras">Aras Kargo</SelectItem>
                      <SelectItem value="mng">MNG Kargo</SelectItem>
                      <SelectItem value="yurtici">Yurtiçi Kargo</SelectItem>
                      <SelectItem value="ptt">PTT Kargo</SelectItem>
                      <SelectItem value="ups">UPS</SelectItem>
                      <SelectItem value="dhl">DHL</SelectItem>
                      <SelectItem value="fedex">FedEx</SelectItem>
                      <SelectItem value="other">Diğer</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Takip Numarası</Label>
                  <Input 
                    value={statusUpdate.tracking_number}
                    onChange={(e) => setStatusUpdate(prev => ({...prev, tracking_number: e.target.value}))}
                    placeholder="Kargo takip numarası"
                  />
                </div>
              </>
            )}

            <div className="space-y-2">
              <Label>Not (Opsiyonel)</Label>
              <Textarea 
                value={statusUpdate.note}
                onChange={(e) => setStatusUpdate(prev => ({...prev, note: e.target.value}))}
                placeholder="Durum güncellemesi ile ilgili not..."
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusModal({ open: false, order: null })}>
              İptal
            </Button>
            <Button onClick={updateOrderStatus} disabled={updatingStatus}>
              {updatingStatus && <RefreshCw className="w-4 h-4 animate-spin mr-2" />}
              Güncelle
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
